import React from 'react'
import { subYears } from 'date-fns'
import { isValidPhoneNumber } from 'libphonenumber-js'
import * as yup from 'yup'

import { FormattedMessage } from '@divvy-web/i18n'

import { maskedInput } from '@sputnik/schema/types'

export const legalCharactersForHumanNamePattern = /^[a-zA-Z ]*$/
export const illegalCharactersForHumanNameMessage = (
  <FormattedMessage
    defaultMessage='Special characters are not allowed'
    id='sputnik.personSchema__K+kL45'
  />
)

export const firstName = yup
  .string()
  .matches(legalCharactersForHumanNamePattern, {
    message: () => illegalCharactersForHumanNameMessage,
  })
  .max(30, () => (
    <FormattedMessage
      defaultMessage='First name may not have more than 30 characters'
      id='sputnik.personSchema__Kk+0zt'
    />
  ))

export const lastName = yup
  .string()
  .matches(legalCharactersForHumanNamePattern, {
    message: () => illegalCharactersForHumanNameMessage,
  })
  .max(30, () => (
    <FormattedMessage
      defaultMessage='Last name may not have more than 30 characters'
      id='sputnik.personSchema__vXw8PA'
    />
  ))

export const email = yup.string().email(() => (
  <FormattedMessage
    defaultMessage='Please enter a valid email'
    id='sputnik.personSchema__prBAWB'
  />
))

export const phoneNumber = yup.string().test(
  'isValidPhoneNumber',
  () => (
    <FormattedMessage
      defaultMessage='Please enter a valid phone number'
      id='sputnik.personSchema__FPcbu0'
    />
  ),
  (value) => {
    if (!value) {
      return true
    }

    return isValidPhoneNumber(value)
  },
)

export const title = yup.string().nullable()

export const dob = yup
  .date()
  .typeError(() => (
    <FormattedMessage
      defaultMessage='Please enter a valid date of birth. For example, 01/31/1990'
      id='sputnik.personSchema__r5mX1H'
    />
  ))
  .max(subYears(new Date(), 18), () => (
    <FormattedMessage
      defaultMessage='Must be over 18 years old'
      id='sputnik.personSchema__ggU5kc'
    />
  ))
  .min(subYears(new Date(), 100), () => (
    <FormattedMessage
      defaultMessage='Please enter a valid date of birth'
      id='sputnik.personSchema__XrX1j3'
    />
  ))

const invalidSsnErrorMessage = () => (
  <FormattedMessage
    defaultMessage='Please enter a valid ssn'
    id='sputnik.personSchema__Mw6aCZ'
  />
)

const requiredSsnErrorMessage = () => (
  <FormattedMessage
    defaultMessage='Social Security number is required.'
    id='sputnik.personSchema__6gmjZ8'
  />
)

export const ssn = maskedInput
  .when('isNonUSCitizen', {
    is: false,
    then: (schema) =>
      schema.test('isRequiredSSN', requiredSsnErrorMessage, (input, { path, createError }) => {
        if (!input || (!input.value && !input.display)) {
          return createError({
            path: `${path}.display`,
            message: requiredSsnErrorMessage,
          })
        }

        return true
      }),
    otherwise: (schema) => schema,
  })
  .test('isValidSSN', invalidSsnErrorMessage, (input, { path, createError }) => {
    if (!input || !input.value) {
      return true
    }

    try {
      const parsed = parseFloat(input.value)
      if (!Number.isFinite(parsed) || Number.isNaN(parsed) || !Number.isInteger(parsed)) {
        return createError({
          path: `${path}.display`,
          message: invalidSsnErrorMessage,
        })
      }
    } catch {
      return createError({
        path: `${path}.display`,
        message: invalidSsnErrorMessage,
      })
    }

    if (input.value.length !== 9) {
      return createError({
        path: `${path}.display`,
        message: invalidSsnErrorMessage,
      })
    }

    // split SSN into its three groups of digits
    const [first, second, third] = [input.value.substring(0, 3), input.value.substring(3, 5), input.value.substring(5)]

    // any part cannot contain all 0s
    // it also cannot start with 666
    if (first === '000' || first === '666' || second === '00' || third === '0000') {
      return createError({
        path: `${path}.display`,
        message: invalidSsnErrorMessage,
      })
    }

    // every digit cannot be the same
    const testChar = input.value[0]
    if (input.value.split('').every((c) => c === testChar) && testChar !== '3') {
      return createError({
        path: `${path}.display`,
        message: invalidSsnErrorMessage,
      })
    }

    // digits cannot be consecutive
    if (
      ['012345678', '123456789', '987654321', '876543210'].some(
        (consecutiveOption) => consecutiveOption === input.value,
      )
    ) {
      return createError({
        path: `${path}.display`,
        message: invalidSsnErrorMessage,
      })
    }

    return true
  })
